import React from "react";
import ServicePageLayout, { H2, H3, P, UL } from "./ServicePageLayout";
import Certifications from "@/components/Certifications";

export const meta = {
  title: "Lethbridge Networking, WiFi, DAS & Wireless | Business IT | Epicom Systems",
  metaDescription:
    "Business networking, commercial Wi-Fi, cellular DAS and wireless infrastructure in Lethbridge and the immediate surrounding area — Coaldale, Coalhurst, Raymond, Magrath and out into the county.",
  canonicalPath: "/lethbridge",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/lethbridge#service",
    "name": "Lethbridge Business Networking, WiFi, DAS and Wireless",
    "serviceType": "Commercial networking, cellular DAS, WiFi and wireless infrastructure",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": [
      { "@type": "City", "name": "Lethbridge, AB" },
      { "@type": "City", "name": "Coaldale, AB" },
      { "@type": "City", "name": "Coalhurst, AB" },
      { "@type": "City", "name": "Raymond, AB" },
      { "@type": "City", "name": "Magrath, AB" }
    ],
    "description":
      "Commercial networking, cellular DAS, commercial Wi-Fi and wireless infrastructure work in Lethbridge and the immediate surrounding area.",
    "url": "https://epicom.ca/lethbridge",
  },
};

export default function Lethbridge() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="Lethbridge, AB"
      h1="Networking, WiFi, DAS & Wireless for Lethbridge Businesses"
      intro="Lethbridge is the practical home-base for Epicom’s work across Southern Alberta. Most weeks we’re somewhere between the north-side industrial areas, the south-side commercial blocks, and the county line — plus businesses in Coaldale, Coalhurst, Raymond, Magrath and other communities throughout Southern Alberta that operate as part of the same commercial region. This page covers the work most Lethbridge-area businesses actually need from us."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/southern-alberta", label: "Southern Alberta Regional Page" },
        { to: "/business-wifi", label: "Business Wi-Fi" },
        { to: "/cellular-das", label: "Cellular DAS" },
      ]}
    >
      <H2>What Lethbridge-area customers usually call us about</H2>
      <UL>
        <><a href="/commercial-networking" className="text-signal hover:underline"><strong className="text-white/90">Commercial networking</strong></a> — offices, clinics, retail and light industrial sites moving off consumer routers, mesh Wi-Fi kits and flat networks onto a real UniFi environment with proper switching, VLANs and remote management.</>
        <><a href="/business-wifi" className="text-signal hover:underline"><strong className="text-white/90">Business Wi-Fi</strong></a> — coverage that survives 30+ simultaneous users, doesn’t drop the guest network at random and doesn’t leave a dead corner in the back office.</>
        <><a href="/cellular-das" className="text-signal hover:underline"><strong className="text-white/90">Cellular DAS</strong></a> — commercial in-building cellular coverage for offices, industrial and institutional buildings where steel, concrete and low-E glass have killed the interior signal.</>
        <><a href="/cell-boosters" className="text-signal hover:underline"><strong className="text-white/90">Cell boosters</strong></a> — residential and small-commercial signal boosters where a properly-installed booster is a better fit than a full DAS.</>
        <><a href="/security-cameras" className="text-signal hover:underline"><strong className="text-white/90">Security cameras</strong></a> and <a href="/access-control" className="text-signal hover:underline"><strong className="text-white/90">access control</strong></a> — commercial camera systems on UniFi Protect and electronic door access on UniFi Access, integrated with the rest of the site.</>
        <><a href="/point-to-point-wireless" className="text-signal hover:underline"><strong className="text-white/90">Point-to-point wireless</strong></a> — wireless bridges between multiple buildings on one operation, or between a Lethbridge office and a shop or yard just outside town.</>
      </UL>

      <H2>Why Lethbridge buildings tend to need DAS or a booster</H2>
      <P>
        Newer commercial construction across Lethbridge favours the same materials that
        make buildings efficient and code-compliant: steel framing and cladding, tilt-up
        concrete panels, ICF walls, and metallic-film / low-E glazing. Those materials also
        do a very effective job of attenuating cellular RF. That is the reason most people
        working in a modern Lethbridge office have to walk outside to make a call or send
        a photo attachment — the building is doing exactly what it was designed to do.
      </P>
      <P>
        The right response depends on the space. A single home or a very small office is
        usually a{" "}
        <a href="/cell-boosters" className="text-signal hover:underline">booster</a> job. A
        real commercial building with staff on multiple carriers is a{" "}
        <a href="/cellular-das" className="text-signal hover:underline">DAS</a> job. We
        assess each site on its actual signal environment before we quote.
      </P>
      <P>
        On the booster side we're a <strong className="text-white/90">SureCall Certified
        Installer</strong> and a <strong className="text-white/90">WilsonPro Certified
        Installer / Integrator</strong>; on the active DAS side we're a{" "}
        <strong className="text-white/90">Nextivity Certified Partner</strong> with CEL-FI
        Enterprise Sales & Installation certification. That's the manufacturer training
        behind the recommendation we give a Lethbridge customer for their space.
      </P>

      <H2>Multi-site businesses around Lethbridge</H2>
      <P>
        A large share of Lethbridge-area business isn’t inside one building. It’s an office
        in town and a shop out toward Coaldale; a main site by the highway and a satellite
        location closer to the university; a headquarters in Lethbridge with yards or shops
        in Coaldale, Coalhurst, or other communities in the surrounding area. Tying those
        sites together properly usually
        means some combination of the wired network,{" "}
        <a href="/point-to-point-wireless" className="text-signal hover:underline">wireless bridges</a>{" "}
        between buildings on the same yard, and a coherent design across all locations so
        the whole business is one manageable network — not three separate networks
        pretending to be one.
      </P>

      <H2>Farm-adjacent and rural-adjacent commercial</H2>
      <P>
        Lethbridge is surrounded by working agriculture and agriculture-adjacent business —
        food processing, feed and grain operations, ag services, transportation, equipment
        dealers. These are commercial customers with rural characteristics: shops the size
        of hangars, offices tucked into a corner of them, cameras that need to see the yard
        gate and the loading area, cellular coverage that has to survive metal-sided
        buildings. The network approach borrows from both sides of what we do, and it’s a
        pattern we regularly install. See our{" "}
        <a href="/southern-alberta" className="text-signal hover:underline">Southern Alberta regional page</a>{" "}
        for how we approach the wider rural work.
      </P>

      <H2>Common project sizes</H2>
      <H3>Small business</H3>
      <P>
        A single office or small commercial space moving off consumer gear onto UniFi:
        gateway, switch, PoE, a couple of access points, sensible VLANs and remote
        management. Typically finished in a day or two with minimal disruption.
      </P>
      <H3>Medium commercial</H3>
      <P>
        A larger office, a professional practice, a retail location or a light industrial
        shop: structured cabling, real switching, multiple access points, cameras where
        needed, cellular coverage handled deliberately, and — often — a{" "}
        <a href="/access-control" className="text-signal hover:underline">door access</a>{" "}
        rollout alongside the network.
      </P>
      <H3>Multi-site / multi-building</H3>
      <P>
        Head office plus a shop, plus a yard, plus a satellite location. The Wi-Fi at each
        location is planned, the buildings are tied together, and everything shows up in
        one management pane. This is where the difference between “some IT gear” and
        infrastructure really shows.
      </P>

      <H2>Working with Epicom on a Lethbridge project</H2>
      <P>
        We start with a site walk-through — 30 minutes in the actual space is worth a
        week of emails. From there we scope the work honestly, quote what the site needs,
        and schedule around your operating hours so the disruption is minimal. The finished
        environment is documented, handed over with a single management login, and
        supported by people who don’t have to Google what a VLAN is.
      </P>

      <Certifications variant="compact" className="!py-8 md:!py-10" />
    </ServicePageLayout>
  );
}
